export const DB_NAME = 'teamlio-cache';
export const DB_VERSION = 1;
export const STORE = 'http';

let dbPromise: Promise<IDBDatabase> | null = null;
let openHandle: IDBDatabase | null = null;

/**
 * Opens (and lazily creates) the cache database. A rejection is never cached:
 * `dbPromise` is reset so a later call can retry, and every caller treats a
 * rejection as "no cache available" rather than an error to surface.
 */
export function openCacheDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  const attempt = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexeddb-unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex('ns', 'ns', { unique: false });
        store.createIndex('storedAt', 'storedAt', { unique: false });
      }
    };
    req.onsuccess = () => {
      openHandle = req.result;
      resolve(req.result);
    };
    req.onerror = () => reject(req.error);
  });

  dbPromise = attempt.catch((err) => {
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

/**
 * Test hook — closes the live connection and drops the memoised handle so a
 * fresh database can be opened. The close matters: `deleteDatabase` blocks
 * while any connection is still open, which deadlocks the next open.
 */
export function resetCacheDbForTests(): void {
  const pending = dbPromise;
  try {
    openHandle?.close();
  } catch {
    /* already closed */
  }
  openHandle = null;
  dbPromise = null;
  // A connection that was still opening must be closed once it lands, or it
  // outlives the reset and blocks the delete anyway.
  pending?.then((db) => db.close()).catch(() => {});
}
